/**
 * DashboardPage — tableau de bord ADHD
 * Hiérarchie claire : contexte → tâche → timeline → état → IA
 */
import React, { memo, useMemo, useCallback } from 'react';

import { ClockWidget }        from '../components/dashboard/ClockWidget';
import { WeatherWidget }      from '../components/dashboard/WeatherWidget';
import { CurrentTaskWidget }  from '../components/dashboard/CurrentTaskWidget';
import { MoodEnergyWidget }   from '../components/dashboard/MoodEnergyWidget';
import { ProgressWidget }     from '../components/dashboard/ProgressWidget';
import { MiniTimelineWidget } from '../components/dashboard/MiniTimelineWidget';
import { SmartTaskChat }      from '../components/dashboard/SmartTaskChat';

import { useCurrentTask }     from '../hooks/useCurrentTask';
import { getMoonPhase }       from '../services/moonService';

export const DashboardPage = memo(function DashboardPage({
  tasks,
  isTaskCompleted,
  isSubTaskCompleted,
  toggleSubTask,
  getCompletedCount,
  weekOffset,
  currentDayIndex,
  gameification,
  mood,
  weather,
  onAddTask,
  onStartFocus,
  onOpenTaskDetail,
  onGoToDay,
  onCompleteTask,
  onCreateTask,
  calendarSynced,
  isSyncingCalendar,
}) {
  const moon = useMemo(() => getMoonPhase(), []);

  const taskCtx = useCurrentTask({ tasks, isTaskCompleted, currentDayIndex, weekOffset });
  const {
    currentTask, previousTask, nextTask,
    timeRemainingInCurrent, progressPercent,
    dayStatus, timeUntilNext,
    completedToday, todayTasks,
  } = taskCtx;

  const handleCompleteTask = useCallback((task) => onCompleteTask?.(task), [onCompleteTask]);

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--bg-base, #08080c)' }}>
      <div className="flex-1 overflow-y-auto overscroll-contain">

        {/* ── 1. En-tête : horloge + météo ── */}
        <div className="flex gap-2 px-3 pt-3 pb-1">
          <ClockWidget isSyncing={isSyncingCalendar} />
          <WeatherWidget
            weather={weather?.weather}
            moon={moon}
            isLoading={weather?.isLoading}
            error={weather?.error}
          />
        </div>

        {/* ── 2. Timeline du jour ── */}
        <div className="px-3 py-1">
          <MiniTimelineWidget
            todayTasks={todayTasks}
            isTaskCompleted={isTaskCompleted}
            currentDayIndex={currentDayIndex}
            currentTask={currentTask}
            onOpenDetail={onOpenTaskDetail}
          />
        </div>

        {/* ── 3. Tâche en cours (héro) ── */}
        <div className="px-3 py-1">
          <CurrentTaskWidget
            currentTask={currentTask}
            previousTask={previousTask}
            nextTask={nextTask}
            timeRemaining={timeRemainingInCurrent}
            progress={progressPercent}
            dayStatus={dayStatus}
            timeUntilNext={timeUntilNext}
            isTaskCompleted={isTaskCompleted}
            currentDayIndex={currentDayIndex}
            getCompletedCount={getCompletedCount}
            onComplete={handleCompleteTask}
            onStartFocus={onStartFocus}
            onOpenDetail={onOpenTaskDetail}
            onAddTask={onAddTask}
          />
        </div>

        {/* ── 4. Humeur + Progression ── */}
        <div className="flex gap-2 px-3 py-1">
          <MoodEnergyWidget mood={mood} />
          <ProgressWidget
            level={gameification?.level || 1}
            levelProgress={gameification?.levelProgress || 0}
            todayXP={gameification?.todayXP || 0}
            currentStreak={gameification?.currentStreak || 0}
            completedToday={completedToday}
            totalToday={todayTasks.length}
          />
        </div>

        {/* ── 5. IA — création de tâche ── */}
        <div className="px-3 pt-1 pb-4">
          <SmartTaskChat
            onTaskCreated={onCreateTask}
            existingTasks={tasks}
            weather={weather}
          />
        </div>

      </div>
    </div>
  );
});

export default DashboardPage;
